import { Button, StatusMessage, Table } from '@driftt/dds'
import { forEachObjIndexed, isNil, merge, pathOr, toPairs } from 'ramda'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import useSWR from 'swr'

import { getPlaybooksByGatedPlan } from 'api/playbooks'
import { getTasks } from 'api/tasks'
import {
  makePlaybooksTableColumns,
  PLAYBOOK_CLONE_TYPES,
  PLAYBOOK_INTERACTION_MODAL,
  TABLE_STATES,
} from 'components/PlaybooksTable/constants'
import { DEFAULT_API_PARAMS } from 'components/PlaybooksTable/index.js'
import tableEmptyState from 'images/table-empty-state.svg'
import { getOrgWorkspaces } from 'modules/workspace/selectors'
import { isEmptyOrNil } from 'utils'
import {
  getWorkspacesWithChatPlaybookRead,
  getWorkspacesWithFastlanePlaybookRead,
  hasGates,
  userHasAnyPlaybookAccess,
} from 'utils/access'

import './style.css'

const DeletePlaybookModal = React.lazy(() => import('../DeletePlaybookModal'))
const ClonePlaybookModal = React.lazy(() => import('../ClonePlaybookModal'))
const MovePlaybookModal = React.lazy(() => import('../MovePlaybookModal'))
const RestoreArchivedBlocksModal = React.lazy(() =>
  import('../RestoreArchivedBlocksModal')
)

const PlaybookInteractionModal = Object.freeze({
  [PLAYBOOK_INTERACTION_MODAL.DELETE]: DeletePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.CLONE]: ClonePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.MOVE]: MovePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BOT]: ClonePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_CLP]: ClonePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BLOCK]: ClonePlaybookModal,
  [PLAYBOOK_INTERACTION_MODAL.RESTORE_ARCHIVED_BLOCKS]:
    RestoreArchivedBlocksModal,
})

const keyToCloneType = Object.freeze({
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BOT]: PLAYBOOK_CLONE_TYPES.BOT,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_CLP]: PLAYBOOK_CLONE_TYPES.CLP,
  [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BLOCK]: PLAYBOOK_CLONE_TYPES.BLOCK,
})

const TABLE_EVENTS = Object.freeze({
  PAGINATION: 'pagination',
  SORTING: 'sorting',
})

const INITIAL_MODAL_STATE = Object.freeze({
  [PLAYBOOK_INTERACTION_MODAL.DELETE]: null,
  [PLAYBOOK_INTERACTION_MODAL.CLONE]: null,
  [PLAYBOOK_INTERACTION_MODAL.MOVE]: null,
})

const usePlaybookAccess = () => {
  return useMemo(() => userHasAnyPlaybookAccess(), [])
}

const useWorkspaceAccess = (playbookType) => {
  return useMemo(() => {
    const workspaces =
      playbookType === 'FASTLANE'
        ? getWorkspacesWithFastlanePlaybookRead()
        : getWorkspacesWithChatPlaybookRead()
    return workspaces.length > 1
  }, [playbookType])
}

const useEmptyState = (
  hasNameKeyword,
  playbookType,
  hasAnyPlaybookAccess,
  history
) => {
  return useMemo(() => {
    if (!hasNameKeyword && playbookType === 'FASTLANE') {
      return {
        image: <img src={tableEmptyState} alt="Empty table" />,
        title:
          'Let qualified, high-intent leads skip the queue and book a meeting \n immediately after submitting a form!',
        subtitle: ' ',
        action: (
          <Button
            variant="secondary"
            onClick={() => history.push('/list/fastlane/create')}
          >
            Create Fastlane playbook
          </Button>
        ),
      }
    }

    return {
      title: `There are no ${
        hasAnyPlaybookAccess ? 'playbook' : 'sequence'
      } that match this search`,
      subtitle: `Click on Create ${
        hasAnyPlaybookAccess ? 'playbook' : 'sequence'
      } to get started!`,
    }
  }, [hasNameKeyword, playbookType, hasAnyPlaybookAccess, history])
}

const useTaskData = (playbooks) => {
  const playbookIds = useMemo(
    () => playbooks?.map(({ playbook }) => playbook.id) ?? null,
    [playbooks]
  )

  return useSWR(playbookIds, async (...ids) => await getTasks(ids), {
    dedupingInterval: 300000,
  })
}

export default function PlaybooksTable({
  apiParams,
  playbooksSWR,
  onChange,
  orgUsers,
  showTransientNotification,
  playbookType,
  isPrioritizedPlaybooksOpen,
  history,
}) {
  const { data, isValidating, error, mutate } = playbooksSWR

  const hasNameKeyword = useMemo(
    () => !isNil(apiParams.nameQuery) && apiParams.nameQuery?.length > 0,
    [apiParams.nameQuery]
  )

  const size = useMemo(() => apiParams.pageSize || 10, [apiParams.pageSize])

  const tableState = useMemo(
    () => pathOr(TABLE_STATES.CHAT_ACTIVE, ['category'], apiParams),
    [apiParams]
  )

  const playbooks = data?.playbooks
  const meta = data?.meta

  const isDataLoading = useMemo(
    () => isEmptyOrNil(playbooks) && isValidating,
    [playbooks, isValidating]
  )

  const hasAnyPlaybookAccess = usePlaybookAccess()
  const showWorkspaces = useWorkspaceAccess(playbookType)
  const emptyState = useEmptyState(
    hasNameKeyword,
    playbookType,
    hasAnyPlaybookAccess,
    history
  )

  const [enabledPlaybookReportTypes, setEnabledPlaybookReportTypes] = useState(
    {}
  )
  const [playbookRowModalContext, setPlaybookRowModalContext] = useReducer(
    merge,
    INITIAL_MODAL_STATE
  )

  const orgWorkspaces = useSelector(getOrgWorkspaces)

  const { data: tasksByPlaybook } = useTaskData(playbooks) // Use extracted variable

  useEffect(() => {
    let isCancelled = false

    async function fetchPlaybooksByGatedPlan() {
      try {
        const playbookReportTypePlansEnabled = {}
        const { data: gatedData } = await getPlaybooksByGatedPlan()

        const checkIfEnabledPlaybook = (gates, playbookType) => {
          playbookReportTypePlansEnabled[playbookType] =
            gates.length === 0 || gates.some((gate) => hasGates(gate))
        }

        forEachObjIndexed(checkIfEnabledPlaybook, gatedData)

        if (!isCancelled) {
          setEnabledPlaybookReportTypes(playbookReportTypePlansEnabled)
        }
      } catch (err) {
        if (!isCancelled) {
          console.warn('Failed to fetch gated playbook plans:', err)
        }
      }
    }

    fetchPlaybooksByGatedPlan()

    return () => {
      isCancelled = true
    }
  }, [])

  const playbookRows = useMemo(() => {
    if (!playbooks) return []

    return playbooks.map(({ playbook }) => {
      const taskData =
        !isEmptyOrNil(tasksByPlaybook) && tasksByPlaybook?.[playbook?.id]?.[0]

      return {
        ...playbook,
        ...(!isEmptyOrNil(taskData) && !!taskData && taskData),
      }
    })
  }, [playbooks, tasksByPlaybook])

  const formattedColumns = useMemo(
    () =>
      makePlaybooksTableColumns({
        tableState,
        orgUsers,
        showWorkspaces,
        workspaces: orgWorkspaces,
        onChange: playbooksSWR.mutate,
        enabledPlaybookReportTypes,
        setPlaybookRowModalContext,
        showTransientNotification,
      }),
    [
      tableState,
      orgUsers,
      showWorkspaces,
      orgWorkspaces,
      playbooksSWR.mutate,
      enabledPlaybookReportTypes,
      setPlaybookRowModalContext,
      showTransientNotification,
    ]
  )

  const handleStateChange = useCallback(
    ({ event, pagination, sorting }) => {
      if (event === TABLE_EVENTS.PAGINATION) {
        onChange({
          page: pagination.pageIndex,
          pageSize: pagination.pageSize,
        })
      }

      if (event === TABLE_EVENTS.SORTING) {
        const newSort = sorting?.[sorting?.length - 1]
        const sortProperties = newSort
          ? {
              sortProperty: newSort.id,
              sortDirection: newSort.desc ? 'desc' : 'asc',
            }
          : {
              sortProperty: DEFAULT_API_PARAMS.sortProperty,
              sortDirection: DEFAULT_API_PARAMS.sortDirection,
            }

        onChange(sortProperties)
      }
    },
    [onChange]
  )

  const handleModalClose = useCallback(
    (key) => () => setPlaybookRowModalContext({ [key]: null }),
    []
  )

  const modalPairs = useMemo(() => toPairs(PlaybookInteractionModal), [])

  if (!isEmptyOrNil(error)) {
    return (
      <StatusMessage variant="error">
        We are unable to fetch your playbooks at this time
      </StatusMessage>
    )
  }

  return (
    <>
      {!isPrioritizedPlaybooksOpen && (
        <Table
          emptyState={emptyState}
          isLoading={isDataLoading}
          disableBoxShadow
          columns={formattedColumns}
          rows={playbookRows}
          pagination={{
            defaultPagination: {
              pageIndex: apiParams.page,
              pageSize: size,
            },
            rowTotal: meta?.total ?? 0,
          }}
          onStateChange={handleStateChange}
        />
      )}

      {modalPairs.map(([key, ModalComponent]) => (
        <React.Suspense fallback={<div />} key={key}>
          <ModalComponent
            open={!!playbookRowModalContext[key]}
            onClose={handleModalClose(key)}
            onConfirm={mutate}
            cloneType={keyToCloneType[key]}
            {...playbookRowModalContext[key]}
          />
        </React.Suspense>
      ))}
    </>
  )
}
